// src/components/artwork/ArtworkValuationHistory.jsx
// Traçabilité des valeurs d'une œuvre (note ARTDAI §Priorité 3).
// Affiche les 6 natures de valeur (dernière connue de chaque) + l'historique complet
// append-only (table artwork_valuation_history). Ajouter une valeur = INSÉRER une ligne
// (jamais d'écrasement). Ce sont des valeurs saisies/dérivées, PAS de l'IA → pas de badge IA.
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";

const TYPES = ["acquisition", "market_estimate", "algorithmic", "expert", "retained", "insurance"];
const TYPE_DEFAULT = {
  acquisition: "Prix d'acquisition",
  market_estimate: "Estimation de marché",
  algorithmic: "Valeur algorithmique",
  expert: "Valeur d'expertise",
  retained: "Valeur retenue",
  insurance: "Valeur d'assurance",
};
const GOLD = "#a9843f";
const BORDER = "#e5e0d8";

const num = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

export default function ArtworkValuationHistory({ artworkId, canEdit = false, defaultCurrency = "EUR" }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [rows, setRows] = useState(undefined);
  const [showHistory, setShowHistory] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const emptyForm = {
    value_type: "market_estimate", amount_min: "", amount_max: "", amount_retained: "",
    currency: defaultCurrency || "EUR", valuation_date: new Date().toISOString().slice(0, 10),
    source_type: "manual", source_name: "", method: "", confidence_level: "",
  };
  const [form, setForm] = useState(emptyForm);

  const fetchRows = useCallback(async () => {
    if (!artworkId) { setRows([]); return; }
    try {
      const { data } = await supabase
        .from("artwork_valuation_history")
        .select("*")
        .eq("artwork_id", artworkId)
        .order("valuation_date", { ascending: false })
        .order("created_at", { ascending: false });
      setRows(Array.isArray(data) ? data : []);
    } catch { setRows([]); }
  }, [artworkId]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const typeLabel = (k) => t(`valuationHistory.type.${k}`, { defaultValue: TYPE_DEFAULT[k] || k });
  const fmtAmount = (r) => {
    const cur = r.currency || "EUR";
    const f = (n) => new Intl.NumberFormat(i18n.language || "fr", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(Math.round(Number(n)));
    if (r.amount_min != null && r.amount_max != null && Number(r.amount_min) !== Number(r.amount_max)) return `${f(r.amount_min)} – ${f(r.amount_max)}`;
    const v = r.amount_retained ?? r.amount_min ?? r.amount_max;
    return v != null ? f(v) : "—";
  };
  const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString(i18n.language || "fr") : ""; } catch { return d || ""; } };

  // Dernière valeur connue par type (rows triées desc → première rencontrée = la plus récente).
  const latestByType = {};
  (rows || []).forEach((r) => { if (!latestByType[r.value_type]) latestByType[r.value_type] = r; });
  const presentTypes = TYPES.filter((k) => latestByType[k]);

  const addValue = async () => {
    if (!user?.id || !artworkId) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("artwork_valuation_history").insert({
        artwork_id: artworkId,
        value_type: form.value_type,
        amount_min: num(form.amount_min),
        amount_max: num(form.amount_max),
        amount_retained: num(form.amount_retained),
        currency: form.currency || "EUR",
        valuation_date: form.valuation_date,
        source_type: form.source_type || null,
        source_name: form.source_name || null,
        method: form.method || null,
        confidence_level: form.confidence_level || null,
        created_by: user.id,
      });
      if (error) throw error;
      setShowAdd(false);
      setForm(emptyForm);
      await fetchRows();
    } catch (e) {
      console.error("[valuationHistory] add", e);
    } finally {
      setSaving(false);
    }
  };

  if (rows === undefined) return null;
  if (rows.length === 0 && !canEdit) return null;

  const field = { fontSize: 13, padding: "7px 9px", border: `1px solid ${BORDER}`, borderRadius: 6, width: "100%", background: "#fff" };
  const lbl = { fontSize: 11, color: "#8a8378", marginBottom: 3, display: "block" };

  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 14, padding: 18, background: "#faf9f7", marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: GOLD }}>
            {t("valuationHistory.eyebrow", { defaultValue: "Traçabilité" })}
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1a1a1a", margin: "2px 0 0", fontFamily: "Georgia, serif" }}>
            {t("valuationHistory.title", { defaultValue: "Historique des valeurs" })}
          </h3>
        </div>
        {canEdit && (
          <button type="button" onClick={() => setShowAdd((v) => !v)}
            style={{ background: "#1a1a1a", color: "#f9f7f4", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            {showAdd ? t("valuationHistory.cancel", { defaultValue: "Annuler" }) : "+ " + t("valuationHistory.add", { defaultValue: "Ajouter une valeur" })}
          </button>
        )}
      </div>

      {/* Formulaire d'ajout */}
      {showAdd && canEdit && (
        <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
            <div>
              <label style={lbl}>{t("valuationHistory.field.type", { defaultValue: "Nature" })}</label>
              <select style={field} value={form.value_type} onChange={(e) => setForm({ ...form, value_type: e.target.value })}>
                {TYPES.map((k) => <option key={k} value={k}>{typeLabel(k)}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>{t("valuationHistory.field.min", { defaultValue: "Montant min" })}</label>
              <input style={field} type="number" value={form.amount_min} onChange={(e) => setForm({ ...form, amount_min: e.target.value })} />
            </div>
            <div>
              <label style={lbl}>{t("valuationHistory.field.max", { defaultValue: "Montant max" })}</label>
              <input style={field} type="number" value={form.amount_max} onChange={(e) => setForm({ ...form, amount_max: e.target.value })} />
            </div>
            <div>
              <label style={lbl}>{t("valuationHistory.field.retained", { defaultValue: "Retenu" })}</label>
              <input style={field} type="number" value={form.amount_retained} onChange={(e) => setForm({ ...form, amount_retained: e.target.value })} />
            </div>
            <div>
              <label style={lbl}>{t("valuationHistory.field.currency", { defaultValue: "Devise" })}</label>
              <input style={field} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase().slice(0, 3) })} />
            </div>
            <div>
              <label style={lbl}>{t("valuationHistory.field.date", { defaultValue: "Date" })}</label>
              <input style={field} type="date" value={form.valuation_date} onChange={(e) => setForm({ ...form, valuation_date: e.target.value })} />
            </div>
            <div>
              <label style={lbl}>{t("valuationHistory.field.source", { defaultValue: "Source" })}</label>
              <input style={field} placeholder={t("valuationHistory.field.sourcePh", { defaultValue: "facture, expert, Artprice…" })} value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })} />
            </div>
            <div>
              <label style={lbl}>{t("valuationHistory.field.confidence", { defaultValue: "Confiance" })}</label>
              <select style={field} value={form.confidence_level} onChange={(e) => setForm({ ...form, confidence_level: e.target.value })}>
                <option value="">—</option>
                <option value="low">{t("valuationHistory.confidence.low", { defaultValue: "Faible" })}</option>
                <option value="medium">{t("valuationHistory.confidence.medium", { defaultValue: "Moyenne" })}</option>
                <option value="high">{t("valuationHistory.confidence.high", { defaultValue: "Élevée" })}</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <button type="button" onClick={addValue} disabled={saving}
              style={{ background: GOLD, color: "#1a1a1a", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1 }}>
              {saving ? t("valuationHistory.saving", { defaultValue: "Enregistrement…" }) : t("valuationHistory.save", { defaultValue: "Enregistrer la valeur" })}
            </button>
          </div>
        </div>
      )}

      {/* Dernière valeur par nature */}
      {presentTypes.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          {presentTypes.map((k) => {
            const r = latestByType[k];
            return (
              <div key={k} style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px", background: "#fff" }}>
                <div style={{ fontSize: 11, letterSpacing: ".03em", textTransform: "uppercase", color: "#8a8378" }}>{typeLabel(k)}</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "#1a1a1a", fontFamily: "Georgia, serif" }}>{fmtAmount(r)}</div>
                <div style={{ fontSize: 11, color: "#b8b1a4", marginTop: 2 }}>
                  {fmtDate(r.valuation_date)}{r.source_name ? " · " + r.source_name : ""}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: "#8a8378", margin: 0 }}>{t("valuationHistory.empty", { defaultValue: "Aucune valeur enregistrée pour l'instant." })}</p>
      )}

      {/* Historique complet */}
      {rows.length > 0 && (
        <>
          <button type="button" onClick={() => setShowHistory((v) => !v)}
            style={{ marginTop: 12, background: "none", border: "none", color: GOLD, fontSize: 12.5, cursor: "pointer", padding: 0 }}>
            {showHistory ? t("valuationHistory.hideHistory", { defaultValue: "Masquer l'historique complet" }) : t("valuationHistory.showHistory", { defaultValue: "Voir l'historique complet ({{n}})", n: rows.length })}
          </button>
          {showHistory && (
            <div style={{ marginTop: 10, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#8a8378" }}>
                    <th style={{ padding: "6px 8px", fontWeight: 600 }}>{t("valuationHistory.col.date", { defaultValue: "Date" })}</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600 }}>{t("valuationHistory.col.type", { defaultValue: "Nature" })}</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600 }}>{t("valuationHistory.col.amount", { defaultValue: "Montant" })}</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600 }}>{t("valuationHistory.col.source", { defaultValue: "Source" })}</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600 }}>{t("valuationHistory.col.confidence", { defaultValue: "Confiance" })}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} style={{ borderTop: `1px solid ${BORDER}` }}>
                      <td style={{ padding: "6px 8px" }}>{fmtDate(r.valuation_date)}</td>
                      <td style={{ padding: "6px 8px" }}>{typeLabel(r.value_type)}</td>
                      <td style={{ padding: "6px 8px", fontWeight: 600 }}>{fmtAmount(r)}</td>
                      <td style={{ padding: "6px 8px", color: "#6b6459" }}>{r.source_name || r.source_type || "—"}</td>
                      <td style={{ padding: "6px 8px", color: "#6b6459" }}>{r.confidence_level ? t(`valuationHistory.confidence.${r.confidence_level}`, { defaultValue: r.confidence_level }) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
